import { useState, useCallback, useEffect } from 'react';
import { ConversationSummary } from '../services/conversationHistoryService';

const CONVERSATION_FAVORITES_STORAGE_KEY = 'unifirst-conversation-favorites';

export const useConversationFavorites = () => {
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);

  // Load favorites from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONVERSATION_FAVORITES_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setFavoriteIds(parsed);
        }
      }
    } catch (error) {
      console.error('Failed to load conversation favorites from localStorage:', error);
    }
  }, []);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(CONVERSATION_FAVORITES_STORAGE_KEY, JSON.stringify(favoriteIds));
    } catch (error) {
      console.error('Failed to save conversation favorites to localStorage:', error);
    }
  }, [favoriteIds]);

  // Toggle favorite status for a conversation
  const toggleFavorite = useCallback((conversationId: string) => {
    setFavoriteIds(prev => {
      if (prev.includes(conversationId)) {
        // Remove from favorites
        return prev.filter(id => id !== conversationId);
      } else {
        // Add to favorites
        return [...prev, conversationId];
      }
    });
  }, []);

  // Check if a conversation is favorited
  const isFavorite = useCallback((conversationId: string) => {
    return favoriteIds.includes(conversationId);
  }, [favoriteIds]);

  // Get favorited conversations from a list
  const getFavoriteConversations = useCallback((conversations: ConversationSummary[]) => {
    return conversations.filter(conversation => favoriteIds.includes(conversation.id));
  }, [favoriteIds]);

  // Sort conversations with favorites first
  const sortWithFavoritesFirst = useCallback((conversations: ConversationSummary[]) => {
    return [...conversations].sort((a, b) => {
      const aIsFav = favoriteIds.includes(a.id);
      const bIsFav = favoriteIds.includes(b.id);
      
      if (aIsFav && !bIsFav) return -1;
      if (!aIsFav && bIsFav) return 1;
      
      // If both have same favorite status, sort by updatedAt (most recent first)
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [favoriteIds]);

  return {
    favoriteIds,
    toggleFavorite,
    isFavorite,
    getFavoriteConversations,
    sortWithFavoritesFirst,
    favoritesCount: favoriteIds.length
  };
};